import {
  Component,
  OnInit,
  Sanitizer,
  Directive,
  ElementRef,
  Renderer2,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Player from '@vimeo/player';
import {
  DomSanitizer,
  SafeHtml,
  SafeStyle,
  SafeScript,
  SafeUrl,
  SafeResourceUrl,
} from '@angular/platform-browser';
import { CourseService } from 'src/app/services/course/course.service';
import {
  ActionSheetController,
  MenuController,
  ModalController,
  NavController,
} from '@ionic/angular';
import { LoadingController } from '@ionic/angular';
import { FaceVerificationPage } from '../face-verification/face-verification.page';
import { UserService } from 'src/app/services/user/user.service';
import { QuizParams } from 'src/app/interfaces/answer';
import { errorMessages } from 'src/app/interfaces/error-messages';
import { FaceLogs, User } from 'src/app/interfaces/user';
import { FaceCaptureModalPage } from '../../home/modals/face-capture-modal/face-capture-modal.page';
import * as moment from 'moment';
import Hls from 'hls.js';
import { FaceDetectionService } from 'src/app/services/face-detection/face-detection.service';
import { LuxandImage, SnapshotData } from 'src/app/interfaces/model';
import { FaceRecogPage } from '../face-recog/face-recog.page';
import { FaceCapturePage } from '../face-capture/face-capture.page';
import { PaymentService } from 'src/app/services/payment/payment.service';

// ---------------------------------------------------------------------------
// Unified video-player adapter – wraps Vimeo SDK or a native <video>+HLS.js
// exposing the same API regardless of the underlying provider.
// ---------------------------------------------------------------------------
export class VideoPlayerAdapter {
  private _vimeo: any = null;
  private _video: HTMLVideoElement | null = null;
  private _hls: Hls | null = null;
  private _controlsObserver: MutationObserver | null = null;
  private _provider: 'vimeo' | 'native' = 'native';
  private _nativeHandlers: { [event: string]: EventListener } = {};

  private readonly _eventMap: { [e: string]: string } = {
    play: 'play', pause: 'pause', ended: 'ended',
    playing: 'timeupdate', seeking: 'seeking', seeked: 'seeked',
    timeupdate: 'timeupdate',
  };

  initVimeo(player: any): void {
    this._provider = 'vimeo';
    this._vimeo = player;
  }

  initNative(videoEl: HTMLVideoElement, src: string, lockPlaybackSpeed = false): void {
    this._provider = 'native';
    this._video = videoEl;
    videoEl.src = '';
    if (lockPlaybackSpeed) {
      videoEl.controls = false;
      videoEl.defaultPlaybackRate = 1;
      videoEl.playbackRate = 1;
      videoEl.setAttribute('controlsList', 'nodownload noplaybackrate');
    } else {
      videoEl.setAttribute('controlsList', 'nodownload');
    }
    // Never attach credentials/cookies to HLS requests. Mux (and Bunny) answer
    // with `Access-Control-Allow-Origin: *`, which the browser rejects whenever
    // the request carries credentials — that shows up as a CORS error on an
    // otherwise successful HTTP 200 response. Clearing crossOrigin keeps the
    // requests anonymous so the wildcard CORS header is honoured.
    videoEl.crossOrigin = null as any;
    videoEl.removeAttribute('crossorigin');
    if (this._hls) { this._hls.destroy(); this._hls = null; }
    if (Hls.isSupported()) {
      this._hls = new Hls({
        enableWorker: false,
        // Force every manifest/segment/key request to be sent WITHOUT cookies
        // or auth headers so the `Access-Control-Allow-Origin: *` response from
        // Mux/Bunny stays valid (a wildcard is invalid with credentials).
        xhrSetup: (xhr: XMLHttpRequest) => {
          xhr.withCredentials = false;
        },
      });
      // Recover transparently from transient network/media errors instead of
      // letting a single hiccup tear the player down.
      this._hls.on(Hls.Events.ERROR, (_evt: any, data: any) => {
        if (!data || !data.fatal) { return; }
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            try { this._hls?.startLoad(); } catch (_) {}
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            try { this._hls?.recoverMediaError(); } catch (_) {}
            break;
          default:
            try { this._hls?.destroy(); } catch (_) {}
            this._hls = null;
            break;
        }
      });
      this._hls.loadSource(src);
      this._hls.attachMedia(videoEl);
    } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      videoEl.src = src; // Safari native HLS
    } else {
      videoEl.src = src;
    }

    if (lockPlaybackSpeed) {
      const contextMenuHandler: EventListener = (event: Event) => {
        event.preventDefault();
      };
      this._nativeHandlers.contextmenu = contextMenuHandler;
      videoEl.addEventListener('contextmenu', contextMenuHandler);

      const rateChangeHandler: EventListener = () => {
        if (!this._video || this._video.playbackRate === 1) { return; }
        this._video.playbackRate = 1;
        this._video.defaultPlaybackRate = 1;
      };
      this._nativeHandlers.ratechange = rateChangeHandler;
      videoEl.addEventListener('ratechange', rateChangeHandler);

      this._controlsObserver?.disconnect();
      this._controlsObserver = new MutationObserver(() => {
        if (!this._video) { return; }
        if (this._video.controls || this._video.hasAttribute('controls')) {
          this._video.controls = false;
          this._video.removeAttribute('controls');
        }
      });
      this._controlsObserver.observe(videoEl, {
        attributes: true,
        attributeFilter: ['controls'],
      });
    }
  }

  on(event: string, callback: Function): void {
    if (this._provider === 'vimeo') { this._vimeo.on(event, callback); return; }
    if (!this._video) return;
    const nativeEvent = this._eventMap[event];
    if (!nativeEvent) return;
    if (this._nativeHandlers[event]) {
      this._video.removeEventListener(nativeEvent, this._nativeHandlers[event]);
    }
    let wrapper: EventListener;
    if (event === 'playing') {
      wrapper = () => callback({ duration: this._video!.duration || 0 });
    } else if (event === 'seeking' || event === 'seeked') {
      wrapper = () => callback({ seconds: this._video!.currentTime || 0 });
    } else if (event === 'timeupdate') {
      wrapper = () => callback({
        seconds: this._video!.currentTime || 0,
        duration: this._video!.duration || 0,
      });
    } else {
      wrapper = () => callback();
    }
    this._nativeHandlers[event] = wrapper;
    this._video.addEventListener(nativeEvent, wrapper);
  }

  play(): Promise<void> {
    if (this._provider === 'vimeo') return this._vimeo.play();
    return this._video ? this._video.play().catch(() => {}) : Promise.resolve();
  }

  pause(): Promise<void> {
    if (this._provider === 'vimeo') return this._vimeo.pause();
    if (this._video) this._video.pause();
    return Promise.resolve();
  }

  setCurrentTime(seconds: number): Promise<number> {
    if (this._provider === 'vimeo') return this._vimeo.setCurrentTime(seconds);
    if (this._video) this._video.currentTime = seconds;
    return Promise.resolve(seconds);
  }

  getCurrentTime(): Promise<number> {
    if (this._provider === 'vimeo') return this._vimeo.getCurrentTime();
    return Promise.resolve(this._video?.currentTime ?? 0);
  }

  getDuration(): Promise<number> {
    if (this._provider === 'vimeo') return this._vimeo.getDuration();
    const d = this._video?.duration ?? 0;
    return Promise.resolve(isNaN(d) ? 0 : d);
  }

  exitFullscreen(): void {
    if (this._provider === 'vimeo') { try { this._vimeo.exitFullscreen?.(); } catch (_) {} return; }
    if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
  }

  enterFullscreen(): void {
    if (this._provider === 'vimeo') {
      try { this._vimeo.requestFullscreen?.(); } catch (_) {}
      return;
    }
    const video = this._video as any;
    if (!video) { return; }
    if (video.requestFullscreen) {
      video.requestFullscreen().catch(() => {});
    } else if (video.webkitEnterFullscreen) {
      try { video.webkitEnterFullscreen(); } catch (_) {}
    }
  }

  destroy(): void {
    if (this._provider === 'vimeo' && this._vimeo) {
      try { this._vimeo.destroy?.(); } catch (_) {}
      this._vimeo = null;
    }
    if (this._video) {
      for (const [evt, handler] of Object.entries(this._nativeHandlers)) {
        const ne = this._eventMap[evt] ?? evt;
        if (ne) this._video.removeEventListener(ne, handler);
      }
      this._nativeHandlers = {};
    }
    if (this._controlsObserver) {
      this._controlsObserver.disconnect();
      this._controlsObserver = null;
    }
    if (this._hls) { this._hls.destroy(); this._hls = null; }
  }
}

@Component({
  selector: 'app-module-progress-detail',
  templateUrl: './module-progress-detail.page.html',
  styleUrls: ['./module-progress-detail.page.scss'],
})
export class ModuleProgressDetailPage implements OnInit, OnDestroy  {
  @ViewChild('myVideo') myVideo?: ElementRef;
  @ViewChild('videoElement', { static: true }) videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement', { static: true }) canvasElement!: ElementRef<HTMLCanvasElement>;
  @ViewChild('nativeVideoPlayer') nativeVideoElRef?: ElementRef<HTMLVideoElement>;

  // Current provider: 'vimeo' | 'mux' | 'bunny'
  videoProvider: 'vimeo' | 'mux' | 'bunny' | null = null;
  // Providers the current topic actually has a stream link for
  availableProviders: Array<{ value: 'vimeo' | 'mux' | 'bunny'; label: string }> = [];
  // The provider the user picks from the dropdown before the video loads
  selectedProvider: 'vimeo' | 'mux' | 'bunny' | null = null;
  /** Suppress re-entrant seeking events when we programmatically reset position */
  private _programmaticSeek = false;
  /**
   * Furthest point (in seconds) the student has legitimately watched. Acts as
   * the hard ceiling for forward seeking while the topic is locked. Pure-JS so
   * it is enforced identically on Chrome, Safari, Firefox, Edge and the native
   * Capacitor webview (Android/iOS) regardless of native control styling.
   */
  private _maxWatchedTime = 0;
  /** Tolerance (seconds) allowed when comparing a seek against the ceiling. */
  private readonly _seekTolerance = 1;

  get hasVideo(): boolean {
    return !!(this.topic?.vimeoLink || this.topic?.muxLink || this.topic?.bunnyLink);
  }

  /**
   * Seeking is locked for native (Mux / Bunny) streams until the student has
   * actually finished the video. When the topic status is anything other than
   * WATCHED or COMPLETED the seek bar is hidden (CSS) and forward scrubbing is
   * blocked (JS guard in the 'seeking' handler).
   */
  get isSeekLocked(): boolean {
    if (this.videoProvider !== 'mux' && this.videoProvider !== 'bunny') { return false; }
    const status = this.topic?.topicDetailsStatus;
    return status !== 'WATCHED' && status !== 'COMPLETED';
  }

  /** Face monitoring is only required before the topic is fully watched/completed. */
  get isFaceMonitoringRequired(): boolean {
    const status = this.topic?.topicDetailsStatus;
    return status !== 'WATCHED' && status !== 'COMPLETED';
  }

  topic:any;
  topicId:any;
  vimeoUrl:any;
  player!: VideoPlayerAdapter;
  videoStatus:any;
  simulationTime = 0;
  playing = false;
  totalSeconds = 0;
  module:any;
  faceSettings:any;
  faceVideo:any;
  school:any;
  schoolName:any;
  schoolRefCode:any;
  schoolLogo:any;
  schoolAddress:any;
  schoolContact:any;
  schoolAccredition:any;
  loadingStatus = false;
  studentId:any;
  student:any;
  imageCaptured: any;
  faceTopics:any;// = [2,12,19,50,66,70,100,106,114,130,190,196,222,230,270,292,500,524,534,560,587,620,650];
  faceNoDetectedIntervalId:any;
  faceNoDetectedCounter?: {sec: number };
  paymentCountdownMessage = '';
  paymentCountdownSeverity: 'warning' | 'danger' = 'warning';
  paymentDaysLeft: number | null = null;

  disabledFaceDetection:any = false;
  disabledFaceRecognition:any = false;

  captureDisabledFaceDetection(studentId:any): void {
    this.userService.getUserFaceSettings(studentId).then((res: any) => {
      this.disabledFaceDetection = res.result[0].disabledFaceDetection == 1;
    });
  }

  captureDisabledFaceRecognition(studentId:any): void {
    this.userService.getUserFaceSettings(studentId).then((res: any) => {
      this.disabledFaceRecognition = res.result[0].disabledFaceRecognition == 1;
    });
  }

  constructor(
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    protected _sanitizer: DomSanitizer,
    private couseService: CourseService,
    private navCtrl: NavController,
    public loadingController: LoadingController,
    private menu: MenuController,
    public modalController: ModalController,
    private faceDetectionService: FaceDetectionService,
    private paymentService: PaymentService
  ) {}

  async ngOnInit() {
    this.faceNoDetectedCounter = {sec: 5 };
    this.enabledFaceRecog = false;


    // The camera is (re)initialised in ionViewDidEnter so it always starts
    // fresh on every entry, including prev/next topic navigation.
    this.setupTabVisibilityListener();
    
    //For Video Only
/*     this.userService.getFaceTopics().then((res:any)=>{
      this.faceTopics = res.result[0].faceTopics;
      localStorage.setItem('topicsList', this.faceTopics);
    });
    this.faceTopics = JSON.parse("[" + localStorage.getItem('topicsList') + "]");  */

    this.playing = false;
    this.simulationTime = 0;
    this.userService.getProfileWithOutToDoList('Y').then((res: any) => {
      this.studentId = res.result[0].id;
      this.student = res.result[0];

      //allow disabling face detection/recognition
      this.captureDisabledFaceDetection(this.studentId);
      this.captureDisabledFaceRecognition(this.studentId);

      this.userService
        .getDsProfileById(res.result[0].dsProfileId)
        .then((school: any) => {
          this.school = school.result[0];
          this.schoolName = school.result[0].companyName;
          this.schoolRefCode = "DS Code: "+ school.result[0].referralCode;
          this.schoolLogo = school.result[0].logo;
          this.schoolAddress = school.result[0].fullAddress;
          this.schoolContact = "Email: "+school.result[0].companyEmail +" | Tel. No.: "+ school.result[0].telephone +" | Mobile No.: " +  school.result[0].mobile;
          this.schoolAccredition = "LTO Accredition No.: "+ school.result[0].ltoAccreditationNo;
        })
        .catch(function (error) {
          console.log(error);
          //this.cmsService.errorMessageToast(errorMessages.connectionTimeOut);
        });
    })
    .catch(function (error) {
      console.log(error);
      //this.cmsService.errorMessageToast(errorMessages.connectionTimeOut);
    });

    this.loadPaymentCountdown();
  }

  private loadPaymentCountdown() {
    this.paymentCountdownMessage = '';
    this.paymentDaysLeft = 0;

    this.paymentService.getMyPaymentTransactions().then((response: any) => {
      const transactions = Array.isArray(response?.result) ? response.result : [];
      const approvedTransactions = transactions
        .filter((transaction: any) => transaction?.paymentStatus === 'APPROVED')
        .sort((left: any, right: any) => {
          const leftDate = new Date(left?.validationDate || left?.paymentDate || left?.createdAt || 0).getTime();
          const rightDate = new Date(right?.validationDate || right?.paymentDate || right?.createdAt || 0).getTime();
          return rightDate - leftDate;
        });

      const approvedTransaction = approvedTransactions[0];
      if (!approvedTransaction) {
        return;
      }

      const approvalDate = new Date(approvedTransaction.validationDate || approvedTransaction.paymentDate || approvedTransaction.createdAt);
      if (isNaN(approvalDate.getTime())) {
        return;
      }

      const millisecondsPerDay = 24 * 60 * 60 * 1000;
      const daysElapsed = Math.floor((Date.now() - approvalDate.getTime()) / millisecondsPerDay);
      const daysLeft = 30 - daysElapsed;
      this.paymentDaysLeft = daysLeft;

      const expirationDate = new Date(approvalDate.getTime() + 30 * millisecondsPerDay);

      if (daysLeft >= 0) {
        this.paymentCountdownSeverity = daysLeft <= 5 ? 'danger' : 'warning';
        this.paymentCountdownMessage = `Your account will expire in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (Expiration Date:${expirationDate.toLocaleDateString()}). Please complete your course and final examination at the driving school within the 30-day validity period.`;
      } else {
        this.paymentCountdownSeverity = 'danger';
        this.paymentCountdownMessage = `Your 30-day access period has ended (Expiration Date:${expirationDate.toLocaleDateString()}). Account access is no longer available. Please contact your driving school if you require further assistance.`;       
      }
    }).catch((error) => {
      console.log(error);
    });
  }

  isCoursePaused = false;
  faceDetected = false;
  currentFaceCount = 0;
  lastSnapshotTime: Date | null = null;
  /** Whether the camera / face-detection pipeline is currently running. */
  private cameraInitialized = false;
  videoReady = false;
  videoPlaying = false;
  videoPaused = false;
  videoCurrentTime = 0;
  videoDuration = 0;

  private noFaceTimeout: any = null;
  private readonly NO_FACE_DELAY = 3000;
  private noFaceStartTime: number | null = null;
  private userTriedToPlay = false;
  isTabVisible = false;
  wasPlayingBeforeTabSwitch = false;

    private setupTabVisibilityListener() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  private removeTabVisibilityListener() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

    private async handleVisibilityChange() {
    this.isTabVisible = !document.hidden;
    
    if (document.hidden) {
      // Tab is now hidden - pause the video if it's playing
      if (this.playing && this.player) {
        this.wasPlayingBeforeTabSwitch = true;
        try {
          await this.player.pause();
          console.log('Video paused - tab switched away');
        } catch (error) {
          console.error('Error pausing video on tab switch:', error);
        }
      }
    } else {
      // Tab is now visible - resume immediately when face monitoring is not required.
      if (this.wasPlayingBeforeTabSwitch && this.player && !this.isFaceMonitoringRequired) {
        try {
          await this.player.play();
          this.wasPlayingBeforeTabSwitch = false;
          console.log('Video resumed - tab switched back');
        } catch (error) {
          console.error('Error resuming video on tab switch:', error);
        }
      } else if (this.wasPlayingBeforeTabSwitch && this.faceDetected && this.player && this.disabledFaceDetection === false) {
        try {
          //this.faceRecognition(30);
          await this.player.play();
          this.wasPlayingBeforeTabSwitch = false;
          console.log('Video resumed - tab switched back and face detected');
        } catch (error) {
          console.error('Error resuming video on tab switch:', error);
        }
      } else if (this.wasPlayingBeforeTabSwitch && !this.faceDetected && this.disabledFaceDetection === false) {
        // Don't resume if no face is detected - let normal face detection logic handle it
        console.log('Tab switched back but no face detected - waiting for face detection');
      }
    }
  }


  private async initializeFaceDetection() {
    this.faceDetectionService.onFaceDetected = () => {
      this.faceDetected = true;

      if (this.noFaceTimeout) {
        clearTimeout(this.noFaceTimeout);
        this.noFaceTimeout = null;
        this.noFaceStartTime = null;
      }
    };

        this.faceDetectionService.onNoFaceDetected = () => {
      this.faceDetected = false;
      this.currentFaceCount = 0;

      if (!this.noFaceTimeout && !this.isCoursePaused && this.videoPlaying) {
        this.noFaceStartTime = Date.now();
        this.noFaceTimeout = setTimeout(() => {
        }, this.NO_FACE_DELAY);
      }
    };

    await this.faceDetectionService.initialize(
      this.videoElement.nativeElement,
      this.canvasElement.nativeElement
    );
    this.cameraInitialized = true;
  }

  /**
   * Fully tear down the camera / face-detection pipeline. Releases the webcam
   * MediaStream so the camera light turns off and the next page (or the next
   * topic) can start with a clean, freshly-initialised camera. Safe to call
   * multiple times.
   */
  private teardownCamera() {
    this.faceDetected = false;
    if (this.noFaceTimeout) {
      clearTimeout(this.noFaceTimeout);
      this.noFaceTimeout = null;
      this.noFaceStartTime = null;
    }
    clearInterval(this.faceNoDetectedIntervalId);
    try { this.faceDetectionService.stop(); } catch (_) {}
    this.cameraInitialized = false;
  }

  /**
   * Destroy and re-create the camera. Used when navigating between topics where
   * the page/component may be reused so the camera always starts fresh.
   */
  private async reinitializeCamera() {
    if (!this.isFaceMonitoringRequired) {
      this.teardownCamera();
      return;
    }
    this.teardownCamera();
    try {
      await this.initializeFaceDetection();
    } catch (error) {
      console.error('Error reinitializing camera:', error);
    }
  }

  async takeSnapshot() {
    if (!this.faceDetectionService.canTakeSnapshot()) {
      console.warn('Cannot take snapshot - video not ready');
      return;
    }

    try {
      const snapshot = await this.faceDetectionService.takeSnapshot();
      console.log('Snapshot taken:', snapshot);
      this.lastSnapshotTime = snapshot.timestamp;

      const luxandImage = this.convertToLuxandFormat(snapshot);
      this.imageCaptured = luxandImage;
      console.log('Luxand image prepared:', luxandImage);

    } catch (error) {
      console.error('Error taking snapshot');
    }
  }

  private convertToLuxandFormat(snapshot: SnapshotData): LuxandImage {
    const base64Data = snapshot.fullFrame.dataUrl.split(',')[1];

    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpg' });

    return {
      dataUrl: snapshot.fullFrame.dataUrl,
      base64: base64Data,
      blob: blob,
      width: snapshot.fullFrame.width,
      height: snapshot.fullFrame.height,
    };
  }

  ngOnDestroy() {
    this.teardownCamera();
    if (this.player) { try { this.player.destroy(); } catch (_) {} }
    this.removeTabVisibilityListener();
  }

  // Fires every time the page is navigated away from (prev/next topic, back,
  // quiz, certificate, etc.). Always release the camera here so it is fully
  // destroyed before moving on.
  ionViewWillLeave() {
    this.teardownCamera();
  }

  videoOutFocus(){
    console.log("Lost Focus")
     this.player.pause();
     this.enabledFaceRecog = true;
  }

  /**
   * Custom play/pause toggle used by the locked-state controls (Mux / Bunny).
   * When the seek bar is removed we still need a way to start/stop playback on
   * both Android and iOS.
   */
  togglePlayPause() {
    if (!this.player) { return; }
    if (this.playing) {
      this.player.pause();
    } else {
      this.player.play();
    }
  }

  toggleFullscreen() {
    if (!this.player) { return; }
    if (document.fullscreenElement) {
      this.player.exitFullscreen();
      return;
    }
    this.player.enterFullscreen();
  }


  ionViewDidEnter() {
    //this.topicId = null;
    //this.topicId = history.state.data;
    this.playing = false;
    this.simulationTime = 0;
    this.topicId = this.route.snapshot.params['topicId'];
    this.loadTopic(this.topicId);
    this.userService.getFaceSettings().then((res) => {
      if (res) {
        this.faceSettings = res;
        this.faceVideo = this.faceSettings.result[0].faceVideo;

        this.couseService.getTopicDetail(this.topicId).then((res: any) => {
          if (res.statusCode == 200) {
            this.topic = res.result[0]

            this.couseService.getModuleStatusByTopicId(this.topic.topicId).then((res: any) => {
              if (res.statusCode == 200) {
                this.module = res.result[0];
                setInterval(() => {
                  if (this.faceVideo == 'true') {
                    this.startPlaying(this.module, this.module.moduleStatus, this.topic, this.totalSeconds);
                  } else {
                    this.startPlaying2(this.topicId, this.module.moduleStatus, this.totalSeconds, this.topic);
                  }
                }, 1000);
              };
            });
          }
        });
      }
    });
  }
  iframeVimeo() {
    this.vimeoUrl =
      'https://player.vimeo.com/video/' +
      this.topicId +
      '?api=1&player_id=player1';
    this.vimeoUrl = this._sanitizer.bypassSecurityTrustResourceUrl(
      this.vimeoUrl
    );


  }

  // ---------------------------------------------------------------------------
  // Video provider selection and player initialisation
  // ---------------------------------------------------------------------------
  videoCurrentPos: any;
  enabledFaceRecog: Boolean = false;

  /**
   * Entry point called from loadTopic().
   * Detects which provider the topic uses and launches the right player.
   *   topic.vimeoLink  -> Vimeo embed player
   *   topic.muxLink    -> Mux HLS stream (full URL or bare playback-id)
   *   topic.bunnyLink  -> Bunny.net HLS stream URL
   */
  initVideoPlayer(topicDetailsId: any, topic: any) {
    // A provider must be chosen (from the dropdown) before any player loads.
    //console.log('Initializing video player for topic', topicDetailsId, 'with topic data:', topic);
    const provider = this.selectedProvider;
    if (!provider) { return; }

    if (provider === 'vimeo' && topic.vimeoLink) {
      this.videoProvider = 'vimeo';
      this.divVimeo(topicDetailsId, topic);
      //console.log('Initialized Vimeo player with link:', topic.vimeoLink);
    } else if (provider === 'mux' && topic.muxLink) {
      this.videoProvider = 'mux';
      //const src = this.buildMuxSrc(topic);
      //console.log('Initialized Mux player with link:', src);
      //this.divNative(topicDetailsId, topic, src);
      this.divNative(topicDetailsId, topic, topic.muxLink  );
    } else if (provider === 'bunny' && topic.bunnyLink) {
      this.videoProvider = 'bunny';
      //console.log('Initialized Bunny player with link:', topic.bunnyLink);
      this.divNative(topicDetailsId, topic, topic.bunnyLink);
    }
  }

  /**
   * Build the Mux HLS source URL, transparently supporting BOTH public and
   * signed playback policies.
   *
   *  - Public  : https://stream.mux.com/{PLAYBACK_ID}.m3u8
   *  - Signed  : https://stream.mux.com/{PLAYBACK_ID}.m3u8?token={JWT}
   *
   * The policy is detected dynamically from the topic record so the same code
   * path works whether the backend stores a bare playback id, a full URL, an
   * already-tokenised URL, or a separate signing token. Field names are matched
   * loosely to tolerate backend naming differences.
   */
  private buildMuxSrc(topic: any): string {
    const raw = String(topic?.muxLink ?? '').trim();

    // A signing token may be supplied under any of several common field names.
    const token =
      topic?.muxToken ??
      topic?.muxPlaybackToken ??
      topic?.muxSignedToken ??
      topic?.muxJwt ??
      topic?.muxStreamToken ??
      null;

    // Explicit policy flag (e.g. "signed" / "public" / true) when the backend
    // provides one. Otherwise the presence of a token implies a signed asset.
    const policy = String(
      topic?.muxPolicy ?? topic?.muxPlaybackPolicy ?? topic?.muxPlaybackType ?? ''
    ).toLowerCase();
    const isSigned =
      topic?.muxSigned === true ||
      topic?.muxSigned === 1 ||
      policy.includes('sign') ||
      policy.includes('private') ||
      (!!token && String(token).length > 0);

    // Full URL already provided by the backend.
    if (/^https?:\/\//i.test(raw)) {
      // If it is signed but the URL has no token yet, append the JWT.
      if (isSigned && token && !/[?&]token=/i.test(raw)) {
        const sep = raw.includes('?') ? '&' : '?';
        return `${raw}${sep}token=${encodeURIComponent(String(token))}`;
      }
      return raw;
    }

    // Bare playback id -> assemble the stream URL.
    const playbackId = raw.replace(/\.m3u8$/i, '');
    const base = `https://stream.mux.com/${playbackId}.m3u8`;
    if (isSigned && token) {
      return `${base}?token=${encodeURIComponent(String(token))}`;
    }
    return base;
  }

  /**
   * Build the list of providers the current topic has links for so the user
   * can pick one from the dropdown. Preselects the topic's default provider
   * when one is supplied; otherwise auto-selects when only one is available.
   */
  buildAvailableProviders(topic: any) {
    const providers: Array<{ value: 'vimeo' | 'mux' | 'bunny'; label: string }> = [];
    if (topic?.bunnyLink) { providers.push({ value: 'bunny', label: 'Video Stream 1' }); }
    if (topic?.muxLink) { providers.push({ value: 'mux', label: 'Video Stream 2' }); }    
    if (topic?.vimeoLink) { providers.push({ value: 'vimeo', label: 'Video Stream 3' }); }
    this.availableProviders = providers;

    // Use the default provider configured on the topic details if it is valid
    // and a matching stream link exists; otherwise auto-select when there is
    // only a single provider, else leave it for the user to choose.
    const defaultProvider = this.normalizeProvider(topic?.defaultProvider ?? topic?.videoProvider);
    if (defaultProvider && providers.some(p => p.value === defaultProvider)) {
      this.selectedProvider = defaultProvider;
    } else {
      this.selectedProvider = providers.length === 1 ? providers[0].value : null;
    }
  }

  /**
   * Normalise a free-form provider value coming from the backend (e.g. "Bunny",
   * "Bunny Streams", "MUX", "vimeo") into one of the internal provider keys.
   */
  private normalizeProvider(value: any): 'vimeo' | 'mux' | 'bunny' | null {
    if (!value) { return null; }
    const v = String(value).toLowerCase();
    if (v.includes('bunny')) { return 'bunny'; }
    if (v.includes('mux')) { return 'mux'; }
    if (v.includes('vimeo')) { return 'vimeo'; }
    return null;
  }

  /**
   * Called when the user picks a provider from the dropdown. Loads the player
   * for the selected stream provider.
   */
  onProviderSelected(provider: 'vimeo' | 'mux' | 'bunny' | null) {
    if (!provider || !this.topic) { return; }
    this.selectedProvider = provider;
    // Reset any previously loaded player before switching providers.
    if (this.player) { try { this.player.destroy(); } catch (_) {} }
    this.videoProvider = null;
    this.initVideoPlayer(this.topic.topicDetailsId, this.topic);
  }

  // ---------------------------------------------------------------------------
  // Per-topic video progress persistence (survives page refresh)
  // ---------------------------------------------------------------------------
  private readonly VIDEO_POS_KEY = 'videoCurrentPos';
  private readonly VIDEO_POS_TOPIC_KEY = 'currentTopicDetailsId';

  /** Persist the current playback position together with the owning topic. */
  private saveVideoPosition(topicDetailsId: any, seconds: any) {
    const secs = Number(seconds);
    if (isNaN(secs)) { return; }
    localStorage.setItem(this.VIDEO_POS_KEY, String(secs));
    localStorage.setItem(this.VIDEO_POS_TOPIC_KEY, String(topicDetailsId));
  }

  /** Reset the stored playback position back to the start (0). */
  private clearVideoPosition() {
    localStorage.setItem(this.VIDEO_POS_KEY, '0');
  }

  /**
   * Resolve the saved playback position for a topic. Returns 0 (start) when the
   * stored progress belongs to a different topic or when the topic is already
   * completed/watched, so finished videos always restart from the beginning.
   */
  private getSavedVideoPosition(topicDetailsId: any): number {
    const savedTopicId = localStorage.getItem(this.VIDEO_POS_TOPIC_KEY);
    if (savedTopicId == null || String(savedTopicId) !== String(topicDetailsId)) {
      return 0;
    }
    const status = this.topic?.topicDetailsStatus;
    if (status === 'COMPLETED') {
      return 0;
    }
    const pos = Number(localStorage.getItem(this.VIDEO_POS_KEY));
    return isNaN(pos) || pos < 0 ? 0 : pos;
  }

  // ---- Vimeo ----------------------------------------------------------------
  divVimeo(topicDetailsId: any, topic: any) {
    this.topic = topic;
    //console.log('Vimeo source:', this.topic.vimeoLink);
    const vimeoId = this.topic.vimeoLink.replace('https://player.vimeo.com/video/', '');
    const adjustableWidth = window.innerWidth > 900 ? window.innerWidth / 2.5 : window.innerWidth - 50;
    const options = { id: vimeoId, width: adjustableWidth };

    if (this.player) { try { this.player.destroy(); } catch (_) {} }

    const vimeoPlayer = new Player('myVideo', options);
    const adapter = new VideoPlayerAdapter();
    adapter.initVimeo(vimeoPlayer);
    this.player = adapter;

    // Resume from the saved position only if it belongs to this topic and the
    // topic is not already completed; otherwise start from the beginning.
    const savedPos = this.getSavedVideoPosition(topicDetailsId);
    this.videoCurrentPos = savedPos;
    if (savedPos > 0) {
      // Keep the seek-guard baseline in sync so the restore is not snapped back.
      this.simulationTime = savedPos;
      this._maxWatchedTime = savedPos;
      this._programmaticSeek = true;
      this.player.setCurrentTime(savedPos)
        .then(() => setTimeout(() => { this._programmaticSeek = false; }, 300))
        .catch(() => { this._programmaticSeek = false; });
    }
    this.setupPlayerEvents(topicDetailsId, topic);
  }

  // ---- Mux / Bunny (native HLS via hls.js) ----------------------------------
  divNative(topicDetailsId: any, topic: any, src: string) {
    this.topic = topic;
    //console.log('Native video source:', src);
    const videoEl = this.nativeVideoElRef?.nativeElement;
    if (!videoEl) { console.warn('nativeVideoPlayer element not found'); return; }

    if (this.player) { try { this.player.destroy(); } catch (_) {} }

    const adapter = new VideoPlayerAdapter();
    adapter.initNative(videoEl, src, this.isSeekLocked);
    this.player = adapter;

    // Resume from the saved position only if it belongs to this topic and the
    // topic is not already completed; otherwise start from the beginning.
    const savedPos = this.getSavedVideoPosition(topicDetailsId);
    this.videoCurrentPos = savedPos;
    if (savedPos > 0) {
      // Keep the seek-guard baseline in sync so the restore seek (which fires a
      // native 'seeking' event) is not snapped back to 0 on Mux / Bunny.
      this.simulationTime = savedPos;
      this._maxWatchedTime = savedPos;
      const restorePos = () => {
        this._programmaticSeek = true;
        adapter.setCurrentTime(savedPos)
          .then(() => setTimeout(() => { this._programmaticSeek = false; }, 300))
          .catch(() => { this._programmaticSeek = false; });
        videoEl.removeEventListener('loadedmetadata', restorePos);
      };
      videoEl.addEventListener('loadedmetadata', restorePos);
    }
    this.setupPlayerEvents(topicDetailsId, topic);
  }

  // ---- Shared event wiring for ALL providers --------------------------------
  setupPlayerEvents(topicDetailsId: any, topic: any) {
    this.topic = topic;

    // PLAY
    this.player.on('play', () => {
      this.videoStatus = 'Playing...';
      if (
        this.isFaceMonitoringRequired &&
        this.faceDetected == false &&
        this.disabledFaceDetection === false
      ) {
        this.player.pause();
      }
      this.playing = true;
      this.videoPlaying = true;
      this.player.getCurrentTime().then((seconds: any) => {
        this.saveVideoPosition(topicDetailsId, seconds);
      });
      if (this.topic.topicDetailsStatus != 'COMPLETED') {
        this.addStatusDetails('PLAY');
      }
    });

    // PLAYING (continuous - track total duration and save position)
    this.player.on('playing', (data: any) => {
      if (data.duration && !isNaN(data.duration)) { this.totalSeconds = data.duration; }
      this.player.getCurrentTime().then((seconds: any) => {
        // Grow the watched ceiling only on contiguous playback so a skip can
        // never push the ceiling forward. Works across all browsers/native.
        if (seconds <= this._maxWatchedTime + this._seekTolerance + 1) {
          this._maxWatchedTime = Math.max(this._maxWatchedTime, seconds);
        }
        this.saveVideoPosition(topicDetailsId, seconds);
      });
    });

    // TIMEUPDATE (continuous for ALL providers incl. Vimeo) - grows the watched
    // ceiling smoothly so the forward-seek guard works the same for Vimeo as it
    // does for Mux / Bunny. The contiguity check prevents a forward jump from
    // ever advancing the ceiling, so seeking ahead is always reverted.
    this.player.on('timeupdate', (data: any) => {
      if (this._programmaticSeek) { return; }
      const seconds = (data && typeof data.seconds === 'number') ? data.seconds : NaN;
      if (data && data.duration && !isNaN(data.duration)) { this.totalSeconds = data.duration; }
      if (!isNaN(seconds) && seconds <= this._maxWatchedTime + this._seekTolerance + 1) {
        this._maxWatchedTime = Math.max(this._maxWatchedTime, seconds);
      }
    });

    // PAUSE - capture the exact position so it survives a page refresh
    this.player.on('pause', () => {
      this.videoStatus = 'Paused';
      this.playing = false;
      this.videoPlaying = false;
      this.player.getCurrentTime().then((seconds: any) => {
        this.saveVideoPosition(topicDetailsId, seconds);
      });
      if (this.topic.topicDetailsStatus != 'COMPLETED') {
        this.updateStatusDetails('PAUSED');
      }
    });

    // ENDED
    this.player.on('ended', () => {
      this.simulationTime = 0;
      this.playing = false;
      this.videoPlaying = false;
      this.videoStatus = 'Ended';
      // Video finished -> reset stored progress so it restarts from the start.
      this.clearVideoPosition();
      localStorage.setItem(this.VIDEO_POS_TOPIC_KEY, String(topicDetailsId));
      if (
        this.topic.topicDetailsStatus == 'PENDING' ||
        this.topic.topicDetailsStatus == 'BEGIN' ||
        this.topic.topicDetailsStatus == 'READ'
      ) {
        if (this.topic.quizType) {
          this.updateStatus('WATCHED');
          this.updateStatusDetails('WATCHED');
        } else {
          this.updateStatus('COMPLETED');
          this.updateStatusDetails('COMPLETED');
        }
        this.videoStatus = 'Finished';
        console.log('UPDATED AT End / Finished Video');
      }
      // Vimeo must be re-created after ended; native video just resets position.
      if (this.videoProvider === 'vimeo') {
        this.initVideoPlayer(topicDetailsId, this.topic);
      } else {
        this.clearVideoPosition();
        this.player.setCurrentTime(0);
      }
    });

    // SEEKING / SEEKED - prevent forward scrubbing on first watch.
    // Both events are wired because browsers differ on which fires reliably
    // (Chrome/Edge/Safari fire 'seeking'; some Firefox/native webview builds
    // only settle on 'seeked'). The shared guard enforces the watched ceiling
    // identically everywhere.
    this.player.on('seeking', (data: any) => {
      this.enforceSeekCap(topicDetailsId, data?.seconds);
    });
    this.player.on('seeked', (data: any) => {
      this.enforceSeekCap(topicDetailsId, data?.seconds);
    });
  }

  /**
   * Cross-browser / native forward-seek guard. If the topic has not yet been
   * watched/completed and the user jumps past the furthest point they have
   * legitimately reached, snap the playback head back to that ceiling.
   */
  private enforceSeekCap(topicDetailsId: any, seekedSeconds?: number) {
    if (this._programmaticSeek) { return; }
    localStorage.setItem('currentTopicDetailsId', topicDetailsId);

    // Only enforce while the topic is locked (not yet watched/passed/completed).
    if (
      this.topic.topicDetailsStatus == 'WATCHED' ||
      this.topic.topicDetailsStatus == 'FAILED' ||
      this.topic.topicDetailsStatus == 'PASSED' ||
      this.topic.topicDetailsStatus == 'COMPLETED'
    ) {
      return;
    }

    const apply = (current: number) => {
      // The allowed ceiling is the furthest contiguous playback reached.
      const ceiling = Math.max(this._maxWatchedTime, this.simulationTime);
      if (current > ceiling + this._seekTolerance) {
        this._programmaticSeek = true;
        this.player
          .setCurrentTime(Math.max(0, Math.floor(ceiling)))
          .then(() => {
            localStorage.setItem('currentTopicDetailsId', topicDetailsId);
            setTimeout(() => { this._programmaticSeek = false; }, 300);
          })
          .catch(() => { this._programmaticSeek = false; });
      } else {
        // Backward seek (or within tolerance) is allowed; keep baselines in sync.
        this.simulationTime = current;
        this._maxWatchedTime = Math.max(this._maxWatchedTime, current);
      }
    };

    // Prefer the reading reported by the event; fall back to querying the player.
    if (typeof seekedSeconds === 'number' && !isNaN(seekedSeconds)) {
      apply(seekedSeconds);
    } else {
      this.player.getCurrentTime().then((s: any) => apply(Number(s) || 0));
    }
  }

  checkedOn30 = false;
  checkedOn60 = false;
  checkedOn90 = false;
  checkedOn95 = false;
  dur30 = 0;
  dur60 = 0;
  dur90 = 0;
  dur95 = 0;
   
  startPlaying2(topicDetailsId:any,moduleStatus:any, totalSeconds:any, topic:any) {
    const DATE_TIME_FORMAT = 'yyyy/MM/DD hh:mm:ss'; 
    this.topic = topic;
    this.totalSeconds = totalSeconds;
    var _this = this;
    if (this.playing == true) {
      var dur95 = (97 / 100) * this.totalSeconds;
      console.log("2-timer: " + this.simulationTime +" --update stage:"+dur95 +" --total: "+this.totalSeconds+" status: "+moduleStatus+" playing: "+this.playing);
      
      if (!this.isFaceMonitoringRequired) {

      } else if (this.faceDetected == false && this.disabledFaceDetection === false) {
       
      } else {
            this.faceNoDetectedStartTimer(); // set delay for 5 seconds before pause the video
      }
      
/*       if (this.faceDetected == false && this.disabledFaceDetection === false) {
        this.player.pause();
        this.enabledFaceRecog = true;
      } */
      
      if (this.checkedOn95 == false) {
        if ((this.simulationTime > dur95 && dur95 != 0) && moduleStatus != 'COMPLETED' && this.playing == true) {
          //console.log(this.simulationTime);
          localStorage.setItem('currentTopicDetailsId', topicDetailsId);
          if (
            this.topic.topicDetailsStatus == 'PENDING' ||
            this.topic.topicDetailsStatus == 'BEGIN' ||
            this.topic.topicDetailsStatus == 'READ'
          ) {
            if (this.topic.quizType) {
              //console.log(this.topic.quizType);     
              //console.log('WATCHED');     
              this.updateStatus('WATCHED');
               // Save Module Status Details
               //let startDate = moment.utc(localStorage.getItem('startDateTime')).local();
               //localStorage.setItem('endDateTime',moment.utc(new Date(), 'yyyy/MM/DD hh:mm:ss'+8).toString());
               //let endDate = moment.utc(localStorage.getItem('endDateTime')).local();
               //console.log('Duration: '+ startDate+ ' to ' + endDate);
               this.updateStatusDetails('WATCHED');
               //localStorage.setItem('startDateTime',moment.utc(new Date(), 'yyyy/MM/DD hh:mm:ss'+8).toString());
               //this.addStatusDetails('WATCHED');

            } else {
              //this.updateStatus('COMPLETED');
               // Save Module Status Details
               //let startDate = moment.utc(localStorage.getItem('startDateTime')).local();
               //localStorage.setItem('endDateTime',moment.utc(new Date(), 'yyyy/MM/DD hh:mm:ss'+8).toString());
               //let endDate = moment.utc(localStorage.getItem('endDateTime')).local();
               //console.log('Duration: '+ startDate+ ' to ' + endDate);
               //this.updateStatusDetails('COMPLETED');
               //localStorage.setItem('startDateTime',moment.utc(new Date(), 'yyyy/MM/DD hh:mm:ss'+8).toString());
               //this.addStatusDetails('COMPLETED');
            }
            //this.videoStatus = 'Finished';
            console.log("UPDATED AT startPlaying2 ");
          }
          this.checkedOn95 = true;
        }
      }
      this.simulationTime++;
    }
  }

  startPlaying(module:any, moduleStatus:any, topic:any, totalSeconds:any) {
    const DATE_TIME_FORMAT = 'yyyy/MM/DD hh:mm:ss'; 
    this.totalSeconds = totalSeconds;
    var _this = this;
    this.topic = topic;
    this.module = module;
    if (this.playing == true) {
      var dur30 = (1 / 100) * this.totalSeconds;
      var dur60 = (60 / 100) * this.totalSeconds;
      var dur90 = (90 / 100) * this.totalSeconds;
      var dur95 = (97 / 100) * this.totalSeconds;
      console.log("1-timer: " + this.simulationTime +" --update stage:"+dur95 +" --total: "+this.totalSeconds+" status: "+moduleStatus+" playing: "+this.playing);
 
      if (!this.isFaceMonitoringRequired) {

      } else if (this.faceDetected == false && this.disabledFaceDetection === false) {
       
      } else {
            this.faceNoDetectedStartTimer(); // set delay for 5 seconds before pause the video
      }      

/*       if (this.faceDetected == false && this.disabledFaceDetection === false) {
        this.player.pause();
        this.enabledFaceRecog = true;
      } */
      
      if (this.checkedOn95 == false) {
        if ((this.simulationTime > dur95 && dur95 != 0 ) && moduleStatus != 'COMPLETED' && this.playing == true) {
          localStorage.setItem('currentTopicDetailsId', topic.topicDetailsId);
          if (
            this.topic.topicDetailsStatus == 'PENDING' ||
            this.topic.topicDetailsStatus == 'BEGIN' ||
            this.topic.topicDetailsStatus == 'READ'
          ) {
            if (this.topic.quizType) {    
              this.updateStatus('WATCHED');
               // Save Module Status Details
               //let startDate = moment.utc(localStorage.getItem('startDateTime')).local();
               //localStorage.setItem('endDateTime',moment.utc(new Date(), 'yyyy/MM/DD hh:mm:ss'+8).toString());
               //let endDate = moment.utc(localStorage.getItem('endDateTime')).local();
               //console.log('Duration: '+ startDate + ' to ' + endDate);
               this.updateStatusDetails('WATCHED');
               //localStorage.setItem('startDateTime',moment.utc(new Date(), 'yyyy/MM/DD hh:mm:ss'+8).toString());
               //this.addStatusDetails('WATCHED');

            } else {
               //this.updateStatus('COMPLETED');
               // Save Module Status Details
               //let startDate = moment.utc(localStorage.getItem('startDateTime')).local();
               //localStorage.setItem('endDateTime',moment.utc(new Date(), 'yyyy/MM/DD hh:mm:ss'+8).toString());
               //let endDate = moment.utc(localStorage.getItem('endDateTime')).local();
               //console.log('Duration: '+ startDate + ' to ' + endDate);
               //this.updateStatusDetails('COMPLETED');
               //localStorage.setItem('startDateTime',moment.utc(new Date(), 'yyyy/MM/DD hh:mm:ss'+8).toString());
               //this.addStatusDetails('COMPLETED');
            }
            console.log("UPDATED AT startPlaying - with Face Verification");
          }
          this.checkedOn95 = true;
        }
      }
    
      if (this.checkedOn30 == false && this.isFaceMonitoringRequired) {

      const myObjectArray: any[] = [
          { topicId: 2 },
          { topicId: 4 },
          { topicId: 6 },          
          { topicId: 8 },
          { topicId: 10 },          
          { topicId: 13 },
          { topicId: 16 },
          { topicId: 19 },      
          { topicId: 22 },              
          { topicId: 25 },
          { topicId: 28 },
          { topicId: 31 },
          { topicId: 34 },
          { topicId: 38 },          
          { topicId: 41 },
          { topicId: 44 },
          { topicId: 47 },
          { topicId: 50 },          
          { topicId: 53 },
          { topicId: 57 },          
          { topicId: 201 },
          { topicId: 204 },
          { topicId: 208 },
          { topicId: 212 },
          { topicId: 216 },
          { topicId: 220 },          
          { topicId: 224 },
          { topicId: 228 },
          { topicId: 233 },
          { topicId: 236 },
          { topicId: 240 },
          { topicId: 246 },
        ];

        const targetNumber = this.topic.topicId;
        const foundObject = myObjectArray.find(obj => obj.topicId === targetNumber);

        if (foundObject) {
          console.log('Found Topid Id:', foundObject);
          if ((this.simulationTime > dur30) && (moduleStatus != 'COMPLETED' && moduleStatus != 'WATCHED')) {
            this.player.pause().then(function () {
              if (_this.disabledFaceRecognition === false) {                   
                //_this.faceVerification(30);
                _this.faceRecognition(30);
                console.log("Face Recognition");            
              }
              else {
                _this.faceCapture(30);
                console.log("Face Capture");
              }
            });
          }
        } else {
          console.log('Topic Id not found.');
          if ((this.simulationTime > dur30) && (moduleStatus != 'COMPLETED' && moduleStatus != 'WATCHED')) {
            this.player.pause().then(function () {
              _this.faceCapture(30);
              console.log("Face Capture");
              //this.checkedOn30 = true;
            });
          }
        }
      }
      this.simulationTime++;
    }
  }

  async faceVerification(i: any) {
    var _this = this;
    const modal = await this.modalController.create({
      component: FaceVerificationPage,
      cssClass: 'my-custom-class',
      backdropDismiss: false,
      componentProps: {
        student: this.student,
        topic: this.topic,
        mode: "Topic"
      },
    });

    modal.onDidDismiss().then((dataReturned) => {
      if (dataReturned !== null) {
        if (dataReturned.data.faceVerified == true) {
          if (i == 30) {
            _this.checkedOn30 = true;
          }
          // if (i == 60) {
          //   _this.checkedOn60 = true;
          // }
          // if (i == 90) {
          //   _this.checkedOn90 = true;
          // }
          _this.player.play();
        }
        else {
          //this.videoCurrentPos = localStorage.getItem('videoCurrentPos');
          //if (this.videoCurrentPos > 0){
          //  this.player.setCurrentTime(this.videoCurrentPos).then(function(seconds: any) {});
          //}else{
            this.player.setCurrentTime(0).then(function(seconds: any) {});
          //}
        }
      }
    });

    return await modal.present();
  }

  video: any;
  async faceRecognition(i: any) {
    this.player.exitFullscreen();
    let canvas = document.createElement('canvas');
    this.video = document.getElementById('video');
    canvas.width = 1080;
    canvas.height = 1080;
    let ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(this.video, 0, 0, canvas.width, canvas.height);
    }
    let image = canvas.toDataURL('image/jpeg');

    var _this = this;
    const modal = await this.modalController.create({
      component: FaceRecogPage,
      cssClass: 'my-custom-class',
      backdropDismiss: false,
      componentProps: {
        student: this.student,
        topic: this.topic,
        mode: "Topic",
        imageCap: image
      },
    });
    modal.onDidDismiss().then((dataReturned) => {
      if (dataReturned !== null) {
        if (dataReturned.data.faceVerified == true) {
          if (i == 30) {
            _this.checkedOn30 = true;
          }
          _this.player.play();
          this.enabledFaceRecog = false;
        }
        else {
          // this.player.setCurrentTime(0).then(function (seconds) { });
          this.enabledFaceRecog = true;
        }
      }
    });
    return await modal.present();
  }

    async faceCapture(i: any) {
    this.player.exitFullscreen();
    let canvas = document.createElement('canvas');
    this.video = document.getElementById('video');
    canvas.width = 1080;
    canvas.height = 1080;
    let ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(this.video, 0, 0, canvas.width, canvas.height);
    }
    let image = canvas.toDataURL('image/jpeg');

    var _this = this;
    const modal = await this.modalController.create({
      component: FaceCapturePage,
      cssClass: 'my-custom-class',
      backdropDismiss: false,
      componentProps: {
        student: this.student,
        topic: this.topic,
        mode: "Topic",
        imageCap: image
      },
    });
    modal.onDidDismiss().then((dataReturned) => {
      if (dataReturned !== null) {
        if (dataReturned.data.faceVerified == true) {
          if (i == 30) {
            _this.checkedOn30 = true;
          }
          _this.player.play();
          this.enabledFaceRecog = false;
        }
        else {
          // this.player.setCurrentTime(0).then(function (seconds) { });
          this.enabledFaceRecog = true;
        }
      }
    });
    return await modal.present();
  }

  reload() {
    location.reload();
  }

  back() {
    this.teardownCamera();
    this.navCtrl.navigateBack(
      'student/module-progress/' +
        this.topic.courseId +
        '/' +
        this.topic.languageCode
    );
  }
  quizParams : any;
  quiz() {
/*     this.navCtrl.navigateRoot(
      'student/module-progress-quiz/' +
        this.topic.topicId +
        '/' +
        this.topic.topicDetailsId +
        '/' +
        this.topic.courseId
    ); */
    this.quizParams = this.topic;
    this.quizParams.courseId = this.topic.courseId;
    this.quizParams.topicId = this.topic.topicId;
    this.quizParams.topicDetailsId = this.topic.topicDetailsId;
    this.quizParams.student =this.student;
    this.quizParams.faceVideo = this.faceVideo;
    this.quizParams.moduleStatus = this.topic.topicDetailsStatus;
    this.teardownCamera();
    this.router.navigate([
      'student/module-progress-quiz'], {state: {data: this.quizParams}}
    );
  }

  topicSeqNo: any;
  seqNo: any;
  loadTopic(topicDetailId: any) {
    this.topic = null;
    this.videoProvider = null;
    this.selectedProvider = null;
    this.availableProviders = [];
    // Reset the forward-seek ceiling for the freshly loaded topic.
    this._maxWatchedTime = 0;
    this.simulationTime = 0;
    this.couseService.getTopicDetail(topicDetailId).then((res: any) => {
      if (res.statusCode == 200) {
        this.topic = res.result[0];
        if (this.isFaceMonitoringRequired) {
          this.reinitializeCamera();
        } else {
          this.teardownCamera();
        }
        this.couseService.getTopicById(this.topic.topicId).then((res2: any) => {
          if (res2.statusCode == 200) {
            this.topicSeqNo = res2.result[0];
            this.seqNo = this.topicSeqNo.seqNo;

            this.couseService.getModuleStatusByTopicId(this.topic.topicId).then((res: any) => {
              if (res.statusCode == 200) {
                this.module = res.result[0];
              }
            });
          }
        });
          if (
            this.topic.topicDetailsStatus == 'PENDING' ||
            this.topic.topicDetailsStatus == 'BEGIN'
          ) {
            if (this.topic.quizType == null && (this.topic.vimeoLink == null || this.topic.muxLink == null || this.topic.bunnyLink == null)) {
              this.updateStatus('COMPLETED');
            } else {
              this.updateStatus('READ');
            }
          }
          setTimeout(() => {
            if (this.topic.vimeoLink || this.topic.muxLink || this.topic.bunnyLink) {
              // Populate the provider dropdown. If only one provider exists it is
              // auto-selected; otherwise the user must choose one first.
              this.buildAvailableProviders(this.topic);
              if (this.selectedProvider) {
                this.initVideoPlayer(this.topic.topicDetailsId, this.topic);
              }
            }
          }, 500);
        
      } else {
        this.navCtrl.navigateRoot('student/dashboard');
      }
    })
      .catch((error: any) => {
        console.log(error);
        this.navCtrl.navigateRoot('student/dashboard');
      });
  }

downloadCert() {
    this.teardownCamera();
    this.navCtrl.navigateRoot('/student/certificate');
}

  nextTopic() {
    this.playing = false;
    clearInterval(this.simulationTime);
    this.simulationTime = 0;
    // Destroy the camera before moving to the next topic; it is re-created in
    // ionViewDidEnter when the next topic page loads.
    this.teardownCamera();
    if(localStorage.getItem('currentTopicDetailsId') != this.topic.nextTopicDetailsId){
      this.clearVideoPosition();
    }
     this.navCtrl.navigateRoot(
      '/student/module-progress-detail/' + this.topic.nextTopicDetailsId
    ); 
/*     this.router.navigate([
      'student/module-progress-detail/'], {state: {data: this.topic.nextTopicDetailsId}}
    );   */
  }

  prevTopic() {
    this.playing = false;
    clearInterval(this.simulationTime);
    this.simulationTime = 0;
    // Destroy the camera before moving to the previous topic; it is re-created
    // in ionViewDidEnter when the previous topic page loads.
    this.teardownCamera();
    if(localStorage.getItem('currentTopicDetailsId') != this.topic.previousTopicDetailsId){
      this.clearVideoPosition();
    }
     this.navCtrl.navigateRoot(
      '/student/module-progress-detail/' + this.topic.previousTopicDetailsId
    ); 
/*     this.router.navigate([
      'student/module-progress-detail/'], {state: {data: this.topic.previousTopicDetailsId}}
    );  */  
  }

  updateTodoList() {
    this.topic.todos = [
      {
        name: 'Read Topic',
        status: 'Completed',
      },
      {
        name: 'Watch Video',
        status: 'Incomplete',
      },
      {
        name: 'Quiz',
        status: 'Failed',
      },
    ];
  }

  async updateStatus(status: any) {
    //this.presentLoading();
    //console.log(this.topic.topicId);
    //this.loadingStatus = true;
    this.couseService.updateStatus(this.studentId,this.topic.topicId, status).then((res) => {
      if(res){
      this.topic.topicDetailsStatus = status;
      //this.loadingStatus = false;
      //this.loading.dismiss();
    }
      else{
        //this.loadingStatus = false;
        //this.loading.dismiss();
      }
    })
    .catch(function (error) {
      console.log(error);
      //this.cmsService.errorMessageToast(errorMessages.connectionTimeOut);
      //this.loadingStatus = false;
    });
  }

  async addStatusDetails(status: any) {
    //this.presentLoading();
    //console.log(this.topic.topicId);
    //this.loadingStatus = true;
    this.couseService.addStatusDetails(this.studentId,this.topic.topicId, status).then((res) => {
      if(res){
      //this.topic.topicDetailsStatus = status;
      //this.loadingStatus = false;
      //this.loading.dismiss();
    }
      else{
        //this.loadingStatus = false;
        //this.loading.dismiss();
      }
    })
    .catch(function (error) {
      console.log(error);
      //this.cmsService.errorMessageToast(errorMessages.connectionTimeOut);
      //this.loadingStatus = false;
    });
  }

  async updateStatusDetails(status: any) {
    //this.presentLoading();
    //console.log(this.topic.topicId);
    //this.loadingStatus = true;
    this.couseService.updateStatusDetails(this.studentId,this.topic.topicId, status).then((res) => {
      if(res){
      //this.topic.topicDetailsStatus = status;
      //this.loadingStatus = false;
      //this.loading.dismiss();
    }
      else{
        //this.loadingStatus = false;
        //this.loading.dismiss();
      }
    })
    .catch(function (error) {
      console.log(error);
      //this.cmsService.errorMessageToast(errorMessages.connectionTimeOut);
      //this.loadingStatus = false;
    });
  }

  topicModuleStatus: any;
  topicStatus: any;
  getStatus(topicId: any) {
    this.topicStatus = ' ';
    //this.presentLoading();
    //console.log(this.topic.topicId);
    this.topicModuleStatus = null;
    //this.loadingStatus = true;
    this.couseService.getModuleStatusByTopicId(topicId).then((res) => {
      if(res){
       this.topicModuleStatus = res; 
       this.topicStatus = this.topicModuleStatus.result[0].moduleStatus;
      //this.loadingStatus = false;
      //this.loading.dismiss();
    }
      else{
        //this.loadingStatus = false;
        //this.loading.dismiss();
      }
    })
    .catch(function (error) {
      console.log(error);
      //this.cmsService.errorMessageToast(errorMessages.connectionTimeOut);
      //this.loadingStatus = false;
    });
  }

  loading:any;
  async presentLoading() {
    this.loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Updating Your Progress...',
    });
    await this.loading.present();
  }

  async presentLoading1() {
    this.loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Loading Topic...',
    });
    await this.loading.present();
  }

  openCustom() {
    this.menu.enable(true, 'first');
    this.menu.open('first');
  }
  closeMenu() {
    this.menu.close('first');
  }

  faceNoDetectedStartTimer() {
    if (!this.isFaceMonitoringRequired) {
      clearInterval(this.faceNoDetectedIntervalId);
      return;
    }
    clearInterval(this.faceNoDetectedIntervalId);
    this.faceNoDetectedCounter = { sec: 5 }; // choose whatever you want
    this.faceNoDetectedIntervalId = setInterval(() => {
      console.log("No Face Timer: " + this.faceNoDetectedCounter?.sec);
      if (this.faceNoDetectedCounter && this.faceNoDetectedCounter.sec - 1 == -1) {
        this.faceNoDetectedCounter.sec = 4;
      } else if (this.faceNoDetectedCounter) {
        this.faceNoDetectedCounter.sec -= 1;
        if (this.faceNoDetectedCounter.sec == 0) {
        this.player.pause();
        this.enabledFaceRecog = true;
        console.log("No Face Time Pause: " + this.faceNoDetectedCounter.sec);
        clearInterval(this.faceNoDetectedIntervalId);
      }
      }
    }, 1000);
  }
}
